import { useContext } from 'react';
import AuthContext from '../context/AuthProvider';
import type { PermissionSlug } from '../utils/permissions';

/**
 * Provides permission-checking logic based on the currently authenticated user.
 *
 * @example
 * const { can } = usePermission();
 *
 * // Show button only if user can write to financial module
 * {can('financial:write') && <Button>Agregar método de pago</Button>}
 */
export const usePermission = () => {
  const { datalogin, isProfileValidated } = useContext(AuthContext) as any;

  const permissions: PermissionSlug[] = datalogin?.permissions ?? [];

  /**
   * Returns true if the user has the given permission slug.
   * Users with 'all:all' (Super Admin) always pass regardless of the slug.
   * Having 'write' on a module implicitly grants 'read' on the same module.
   */
  const can = (slug: PermissionSlug): boolean => {
    if (permissions.includes('all:all')) return true;
    if (permissions.includes(slug)) return true;
    // write implies read: if checking :read, also accept :write
    if (slug.endsWith(':read')) {
      const writeSlug = slug.replace(':read', ':write') as PermissionSlug;
      return permissions.includes(writeSlug);
    }
    return false;
  };

  return {
    can,
    /**
     * `false` mientras `permissions` viene solo del snapshot de `localStorage`
     * con el que arranca `AuthProvider`, sin confirmar todavía contra
     * `getProfile`. `ProtectedRoute` la usa para no decidir acceso con
     * permisos potencialmente revocados desde el último login.
     */
    isProfileValidated: Boolean(isProfileValidated),
  };
};
